<template>
  <div class="rp-card">
    <div class="rp-card__head">
      <div>
        <div class="rp-card__title">{{ $t('cashInflowOutflowTrend') }}</div>
        <div class="rp-card__legend">
          <span class="rp-card__dot rp-card__dot--receive" />
          {{ $t('incoming') }}
          <span class="rp-card__dot rp-card__dot--pay" />
          {{ $t('payment') }}
        </div>
      </div>
      <div
        v-if="periodLabel"
        class="rp-card__period"
      >
        {{ periodLabel }}
      </div>
    </div>
    <div
      v-if="canSee"
      class="rp-card__body"
    >
      <apexchart
        v-if="hasData"
        :key="chartKey"
        type="line"
        height="280"
        :options="chartOptions"
        :series="series"
      />
      <div
        v-else
        class="rp-card__empty"
      >
        <v-progress-circular
          v-if="loading"
          :width="3"
          color="blue"
          indeterminate
          size="22"
        />
        <home-empty-state v-else />
      </div>
    </div>
    <div
      v-else
      class="rp-card__empty"
    >
      {{ $t("you don't have access to this information") }}
    </div>
  </div>
</template>

<script>
import HomeEmptyState from './HomeEmptyState.vue'

export default {
  name: 'HomeReceiptPayment',
  components: { HomeEmptyState },
  props: {
    dateType: {
      type: String,
      default: '7days'
    },
    periodLabel: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      items: [],
      loading: false
    }
  },
  computed: {
    canSee () {
      return this.$store.getters.getpermission && this.$store.getters.getpermission.includes('overview')
    },
    hasData () {
      return this.items.some(item => {
        const pay = parseFloat(item.pay) || 0
        const receive = parseFloat(item.receive) || 0
        return pay > 0 || receive > 0
      })
    },
    chartKey () {
      return this.dateType + '-' + this.items.length
    },
    isDailyPeriod () {
      return this.dateType !== 'year'
    },
    series () {
      return [
        {
          name: this.$t('payment'),
          data: this.items.map(item => item.pay)
        },
        {
          name: this.$t('incoming'),
          data: this.items.map(item => item.receive)
        }
      ]
    },
    chartOptions () {
      const self = this
      return {
        chart: {
          type: 'line',
          fontFamily: 'IRANSans',
          toolbar: { show: false },
          zoom: { enabled: false },
          animations: { enabled: false }
        },
        colors: ['#EF4444', '#10B981'],
        dataLabels: { enabled: false },
        stroke: {
          curve: 'smooth',
          width: [2, 2]
        },
        markers: {
          size: this.items.length > 12 ? 0 : 5,
          colors: ['#EF4444', '#10B981'],
          strokeColors: '#ffffff',
          strokeWidth: 2,
          hover: { size: 6 }
        },
        grid: {
          borderColor: '#E5E7EB',
          strokeDashArray: 4,
          xaxis: { lines: { show: false } },
          yaxis: { lines: { show: true } }
        },
        legend: { show: false },
        tooltip: {
          y: {
            formatter (value) {
              return self.$numberFormat(value) + ' ' + (self.$store.state.currency.name || '')
            }
          }
        },
        xaxis: {
          categories: this.items.map(item => item.key),
          tickAmount: this.items.length > 12 ? 7 : undefined,
          axisBorder: { show: false },
          axisTicks: { show: false },
          labels: {
            rotate: 0,
            hideOverlappingLabels: true,
            style: { fontSize: '10px', colors: '#6B7280' }
          }
        },
        yaxis: {
          opposite: !!this.$vuetify.rtl,
          min: 0,
          forceNiceScale: true,
          labels: {
            style: { fontSize: '10px', colors: '#6B7280' },
            offsetX: this.$vuetify.rtl ? -10 : 0,
            formatter (value) {
              return self.$numberFormat(value)
            }
          }
        }
      }
    }
  },
  watch: {
    dateType () {
      this.fetchList()
    }
  },
  mounted () {
    this.fetchList()
  },
  methods: {
    toNumber (value) {
      const parsed = parseFloat(value)
      return Number.isNaN(parsed) ? 0 : parsed
    },
    fromInvoiceByMonth (payload) {
      const data = payload && payload.data != null ? payload.data : payload
      const values = Array.isArray(data) ? data : Object.values(data || {})
      let points = values.map(item => {
        if (item == null || typeof item !== 'object') return null
        const profit = item.monthlyInvoices && item.monthlyInvoices.profit
          ? item.monthlyInvoices.profit
          : {}
        return {
          key: String(item.label || item.key || ''),
          receive: this.toNumber(profit.income),
          pay: this.toNumber(profit.cost)
        }
      }).filter(item => item && item.key)
      return points
    },
    fromPaymentChart (data) {
      let list = data
      if (!Array.isArray(list)) {
        if (data && Array.isArray(data.data)) list = data.data
        else if (data && Array.isArray(data.items)) list = data.items
        else list = []
      }
      return list.map(item => ({
        key: String(item.key || item.label || ''),
        receive: this.toNumber(item.receive),
        pay: this.toNumber(item.pay)
      })).filter(item => item.key)
    },
    fetchPaymentChart () {
      return this.$axios.get(this.$endpoint + 'widgets', {
        params: {
          type: 'getPaymentChart',
          dateType: this.dateType
        }
      }).then(res => {
        this.items = this.fromPaymentChart(res.data)
      }).catch(() => {
        this.items = []
      })
    },
    fetchList () {
      this.loading = true
      if (this.isDailyPeriod) {
        this.fetchPaymentChart().finally(() => {
          this.loading = false
        })
        return
      }
      this.$axios.get(this.$endpoint + 'dashboard/charts/invoice-by-month', {
        params: {
          dateType: this.dateType
        }
      }).then(res => {
        this.items = this.fromInvoiceByMonth(res.data)
      }).catch(() => {
        this.items = []
      }).finally(() => {
        this.loading = false
      })
    }
  }
}
</script>

<style scoped>
.rp-card {
  background: #fff;
  border: 1px solid #E5E7EB;
  border-radius: 4px;
  padding: 12px 14px 8px;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.rp-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 4px;
}
.rp-card__title {
  font-size: 13px;
  font-weight: 700;
  color: #111;
}
.rp-card__legend {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  font-size: 11px;
  color: #6B7280;
  white-space: nowrap;
}
.rp-card__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}
.rp-card__dot--receive {
  background: #10B981;
}
.rp-card__dot--pay {
  background: #EF4444;
}
.rp-card__period {
  font-size: 11px;
  color: #6B7280;
  white-space: nowrap;
  flex-shrink: 0;
}
.rp-card__body {
  min-height: 0;
  flex: 1;
}
.rp-card__empty {
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #6B7280;
  text-align: center;
}
</style>
